import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhoneAlt } from '@fortawesome/pro-solid-svg-icons';

// utils
import { isRequired, isValid } from '../../../../../utils/form';
import { allowNav } from '../../../../../utils/allow-nav';

function NavItem({ children, activeRoute, navRoute }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    theme: { allowNavOnlyInCall },
    telephony: { status: callStatus },
    enrollment: { submittedFlag },
  } = useSelector(store => store);

  // activeRoute = section.route (e.g. '/eligibility')
  // the split logic is to account for routes that potentially have overlapping words;
  // e.g. '/enroll/Humana/2025/eligibility/other-coverage'.includes('/other') lists both
  // the 'other' and 'eligibility' sections as active.
  const active = pathname.split('/').includes(activeRoute.replace('/', ''));
  const activeClass = active ? 'nav-button-active' : '';

  const an = allowNav({ allowNavOnlyInCall, submitted: submittedFlag, callStatus });
  let disabledClass = '';
  if (!navRoute.includes('telephony') && !an) {
    disabledClass = 'nav-button-disabled';
  }

  return (
    <div
      className={`d-flex col-auto nav-button ${activeClass} ${disabledClass}`}
      style={{
        paddingTop: '0.8em',
        paddingBottom: '0.8em',
        paddingLeft: '1.2em',
        paddingRight: '1.2em',
      }}
      role={!disabledClass ? 'button' : undefined}
      tabIndex={!disabledClass ? 0 : undefined}
      onClick={!disabledClass ? () => navigate(navRoute) : undefined}
    >
      <div className="col">
        {children}
      </div>
    </div>
  );
}

function TelephonySection() {
  return (
    <div
      style={{ height: '100%', marginRight: '0.5em', marginLeft: '0.5em' }}
      className="d-flex justify-content-center align-items-center"
    >
      <FontAwesomeIcon size="2x" icon={faPhoneAlt} />
    </div>
  );
}

function NavBar() {
  const {
    form: { form, baseRoute },
    values,
    validation,
  } = useSelector(store => store);

  function getSectionStatus(section) {
    let fieldCount = 0;
    let validCount = 0;

    section.pages.forEach(page => (
      page.fields.forEach(field => {
        const value = values[field.name];
        const validationRegex = validation[field.name];
        if (isRequired({ form, name: field.name, currentValues: values })) {
          fieldCount += 1;
          if ((value !== undefined) && isValid({ value, validationRegex })) validCount += 1;

        // if a non required field has a value, make sure it's valid
        } else if (value !== undefined && value !== '' && value !== null) {
          fieldCount += 1;
          if (isValid({ value, validationRegex })) validCount += 1;
        }
      })
    ));
    if (fieldCount === 0) {
      return (<span className="text-muted fs-5">No required fields</span>);
    } if (fieldCount === validCount) {
      return (<span className="text-success fs-5">Complete!</span>);
    } if (fieldCount > validCount) {
      return (<i className="text-muted fs-5">{validCount} of {fieldCount} Complete</i>);
    }
    return 'Hmm';
  }

  return (
    <>
      {form.map(section => {
        const isTelephony = section.route.includes('telephony');
        const firstPageRoute = section.pages[0]?.route;

        let navRoute = `/enroll${baseRoute}${section.route}${firstPageRoute}`;
        if (isTelephony) navRoute = `/enroll${baseRoute}/telephony/call`;

        return (
          <NavItem key={section.route} activeRoute={section.route} navRoute={navRoute}>
            {isTelephony ? (
              <TelephonySection />
            ) : (
              <>
                <h5 className="fw-bold mb-0 text-center" style={{ fontSize: '19px' }}>{section.navTitle}</h5>
                <div style={{ width: '100%' }} className="d-flex justify-content-center align-items-center">
                  {getSectionStatus(section)}
                </div>
              </>
            )}
          </NavItem>
        );
      })}
    </>
  );
}

export default NavBar;
